/**
 * 🔒 PROTECTED GUEST-FACING MODULE — DO NOT MODIFY WITHOUT EXPLICIT USER APPROVAL
 * ═══════════════════════════════════════════════════════════════════════════════
 * This file is locked under /app/memory/PROTECTED_GUEST_FACING.md (Feb 2026).
 * Any change that affects guest-facing behavior REQUIRES user authorization first.
 * Read /app/memory/PROTECTED_GUEST_FACING.md before proposing edits.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * RSVPPublicModule.jsx — The public RSVP form guests fill in.
 * Locked behaviors: greeting rule, additional-guest validation (first/last/age all required),
 * Phone/Email static display boxes, MenuTile fluid sizing, Submit flow, Guest label numbering.
 */

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Utensils, X, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Design tokens ────────────────────────────────────────────────────────────
const GOLD       = '#D4AF37';
const GOLD_DIM   = 'rgba(212,175,55,0.50)';
const GOLD_MED   = 'rgba(198,167,90,0.80)';
const WHITE97    = 'rgba(255,255,255,0.97)';
const WHITE70    = 'rgba(255,255,255,0.70)';
const GREEN_BG   = 'rgba(30,115,68,0.45)';
const GREEN_GLOW = '0 0 24px rgba(30,140,78,0.45), inset 0 1.5px 0 rgba(255,255,255,0.30), 0 0 0 1.5px rgba(30,140,78,0.60)';
const RED_BG     = 'rgba(165,70,70,0.45)';
const RED_GLOW   = '0 0 24px rgba(165,70,70,0.40), inset 0 1.5px 0 rgba(255,255,255,0.25), 0 0 0 1.5px rgba(165,70,70,0.55)';

const DIETARY_OPTIONS = [
  { key: 'veg',   label: 'Vegetarian', icon: '🥗' },
  { key: 'gf',    label: 'Gluten-Free', icon: '🌾' },
  { key: 'vegan', label: 'Vegan',       icon: '🌿' },
  { key: 'df',    label: 'Dairy-Free',  icon: '🥛' },
  { key: 'nut',   label: 'Nut Allergy', icon: '🥜' },
  { key: 'fish',  label: 'No Seafood',  icon: '🐟' },
  { key: 'diab',  label: 'Diabetic',    icon: '⚕' },
];

const MOCK_MENU = {
  'Appetizer':   ['Caprese with Fresh Burrata', 'Shrimp Cocktail', 'Bruschetta al Pomodoro'],
  'Main Course': ['Chilean Sea Bass', 'Filet Mignon with Truffle Demi-Glace', 'Chicken Piccata'],
  'Dessert':     ['Tiramisu Classico', 'Crème Brûlée', 'Mixed Berry Panna Cotta'],
};

// Convert template menu_tiles array to the menuData format: { 'Title': ['item1', ...] }
function templateTilesToMenu(tiles) {
  if (!tiles || !Array.isArray(tiles) || tiles.length === 0) return MOCK_MENU;
  const menu = {};
  tiles.forEach(t => { if (t.title && Array.isArray(t.items)) menu[t.title] = t.items; });
  return Object.keys(menu).length > 0 ? menu : MOCK_MENU;
}

// ─── Requirement 8: Adult / Child icons ───────────────────────────────────────
const AdultSVG = ({ active }) => {
  const c = active ? GOLD : GOLD_DIM;
  return (
    <svg width="14" height="25" viewBox="0 0 16 30" fill="none" style={{ display:'block' }}>
      <circle cx="8" cy="4.5" r="4" fill={c}/>
      <rect x="3.5" y="10" width="9" height="11" rx="2.5" fill={c}/>
      <rect x="3.5" y="21" width="3.5" height="9" rx="1.5" fill={c}/>
      <rect x="9" y="21" width="3.5" height="9" rx="1.5" fill={c}/>
    </svg>
  );
};
const ChildSVG = ({ active }) => {
  const c = active ? GOLD : GOLD_DIM;
  return (
    <svg width="10" height="19" viewBox="0 0 11 22" fill="none" style={{ display:'block', marginTop:3 }}>
      <circle cx="5.5" cy="3.5" r="3" fill={c}/>
      <rect x="2" y="8" width="7" height="8" rx="1.8" fill={c}/>
      <rect x="2" y="16" width="2.5" height="6" rx="1.2" fill={c}/>
      <rect x="6.5" y="16" width="2.5" height="6" rx="1.2" fill={c}/>
    </svg>
  );
};

// ─── Requirement 14: Card-style Adult/Child selector (matching menu tile shape) ──
function GuestTypeSelector({ value, onChange, hasError }) {
  const cardBase = {
    flex: 1,
    minHeight: 52,
    borderRadius: 12,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 3, cursor: 'pointer', border: 'none',
    transition: 'all 0.20s ease',
  };
  const selected = (type) => value === type;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      {/* Adult card */}
      <button
        onClick={() => onChange('adult')}
        style={{
          ...cardBase,
          background:  selected('adult') ? 'rgba(198,167,90,0.22)' : 'rgba(255,255,255,0.07)',
          border:      selected('adult') ? `1.5px solid ${GOLD_MED}` : '1px solid rgba(198,167,90,0.25)',
          boxShadow:   selected('adult') ? `0 0 12px rgba(198,167,90,0.30), inset 0 1px 0 rgba(255,255,255,0.20)` : 'inset 0 1px 0 rgba(255,255,255,0.12)',
          outline: 'none',
        }}
      >
        <AdultSVG active={selected('adult')} />
        <span style={{ fontFamily:"'Cinzel',serif", fontSize:8, fontWeight:700,
          textTransform:'uppercase', letterSpacing:'0.8px',
          color: selected('adult') ? '#000000' : '#000000' }}>Adult</span>
      </button>

      {/* Requirement 8: Red ? when neither selected */}
      <span style={{
        fontSize: 16, fontWeight: 900, flexShrink: 0, width: 18, textAlign: 'center',
        color: hasError ? '#FF6060' : (value ? 'transparent' : '#E03030'),
        transition: 'color 0.2s',
        userSelect: 'none',
      }}>?</span>

      {/* Child card */}
      <button
        onClick={() => onChange('child')}
        style={{
          ...cardBase,
          background:  selected('child') ? 'rgba(198,167,90,0.22)' : 'rgba(255,255,255,0.07)',
          border:      selected('child') ? `1.5px solid ${GOLD_MED}` : '1px solid rgba(198,167,90,0.25)',
          boxShadow:   selected('child') ? `0 0 12px rgba(198,167,90,0.30), inset 0 1px 0 rgba(255,255,255,0.20)` : 'inset 0 1px 0 rgba(255,255,255,0.12)',
          outline: 'none',
        }}
      >
        <ChildSVG active={selected('child')} />
        <span style={{ fontFamily:"'Cinzel',serif", fontSize:8, fontWeight:700,
          textTransform:'uppercase', letterSpacing:'0.8px',
          color: selected('child') ? '#000000' : '#000000' }}>Child</span>
      </button>
    </div>
  );
}

// ─── Per-guest Attendance toggle (Carter fix, Feb 2026) ──────────────────────
// Compact inline "Attending: Yes / No" control that docks to the right of the
// "Guest N" header row. Designed to add ZERO vertical bulk to the existing
// GuestCard layout — see /app/memory/PROTECTED_GUEST_FACING.md.
//
// Constraints (per user):
//   • text-style segmented control, no bulky pills
//   • single line, ~95-110 px wide max
//   • visible enough to discover, restrained enough not to dominate
//   • default state = YES (we assume most guests are attending)
//   • only rendered when activeGuests.length >= 2 (solo invites unchanged)
function AttendanceToggle({ decision, onChange }) {
  const isYes = decision === 'YES';
  const isNo  = decision === 'NO';
  const segBase = {
    fontFamily: "'Cinzel',serif",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    padding: '3px 7px',
    borderRadius: 5,
    cursor: 'pointer',
    border: '1px solid transparent',
    background: 'transparent',
    transition: 'all 0.15s ease',
    minWidth: 26,
    textAlign: 'center',
    lineHeight: 1.2,
    outline: 'none',
  };
  const yesActive = {
    ...segBase,
    color: '#7BD09A',
    border: '1px solid rgba(45,122,79,0.55)',
    background: 'rgba(30,115,68,0.18)',
  };
  const noActive = {
    ...segBase,
    color: '#E89090',
    border: '1px solid rgba(150,60,60,0.55)',
    background: 'rgba(165,70,70,0.18)',
  };
  const ghost = {
    ...segBase,
    color: 'rgba(255,255,255,0.30)',
  };
  return (
    <div data-testid="attendance-toggle" style={{
      display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
    }}>
      <span style={{
        fontFamily: "'Cormorant Garamond',serif",
        fontStyle: 'italic',
        fontSize: 11,
        color: 'rgba(212,185,100,0.78)',
        letterSpacing: '0.4px',
      }}>
        Attending:
      </span>
      <button
        type="button"
        data-testid="attendance-yes"
        onClick={() => onChange('YES')}
        style={isYes ? yesActive : ghost}
        aria-pressed={isYes}
        aria-label="Mark this guest as attending"
      >Yes</button>
      <button
        type="button"
        data-testid="attendance-no"
        onClick={() => onChange('NO')}
        style={isNo ? noActive : ghost}
        aria-pressed={isNo}
        aria-label="Mark this guest as declining"
      >No</button>
    </div>
  );
}

// ─── Gold checkbox ────────────────────────────────────────────────────────────
function GoldCheckbox({ checked, onChange }) {
  return (
    <div onClick={onChange} style={{
      width:18, height:18, borderRadius:5, flexShrink:0, cursor:'pointer',
      border: checked ? `1.5px solid ${GOLD_MED}` : '1.5px solid rgba(198,167,90,0.30)',
      background: checked ? 'rgba(198,167,90,0.20)' : 'rgba(255,255,255,0.08)',
      display:'flex', alignItems:'center', justifyContent:'center',
      boxShadow: checked ? '0 0 8px rgba(198,167,90,0.28)' : 'inset 0 1px 0 rgba(255,255,255,0.70)',
      transition:'all 0.15s ease',
    }}>
      {checked && <span style={{ color:'#B8902A', fontSize:12, fontWeight:700, lineHeight:1 }}>✓</span>}
    </div>
  );
}

// ─── Menu tiles (unchanged, approved) ─────────────────────────────────────────
function MenuTile({ label, isActive, onClick, icon }) {
  return (
    <button onClick={onClick}
      className={`group relative min-h-[132px] w-full rounded-[1.65rem] border overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform ${isActive ? 'bg-white/82 border-[#C8A84B]/65 -translate-y-[6px]' : 'bg-white/55 border-[#C8A84B]/35 hover:bg-white/70 hover:border-[#C8A84B]/55 hover:-translate-y-[4px]'}`}
      style={{
        backdropFilter: isActive ? 'blur(14px) saturate(1.20)' : 'blur(10px) saturate(1.10)',
        WebkitBackdropFilter: isActive ? 'blur(14px) saturate(1.20)' : 'blur(10px) saturate(1.10)',
        boxShadow: isActive
          ? '0 18px 40px rgba(180,140,40,0.35), 0 4px 12px rgba(180,140,40,0.20), inset 0 1px 0 rgba(255,255,255,0.70)'
          : '0 6px 18px rgba(120,90,20,0.14), inset 0 1px 0 rgba(255,255,255,0.55)',
        transition: 'all 0.50s cubic-bezier(0.22,1,0.36,1)',
      }}>
      <div className="pointer-events-none absolute inset-0 rounded-[1.65rem] border border-white/40"/>
      <div className="pointer-events-none absolute inset-x-[8%] top-0 h-[1px] bg-gradient-to-r from-transparent via-white/85 to-transparent opacity-75"/>
      <div className="pointer-events-none absolute inset-x-[22%] bottom-[26px] h-px bg-gradient-to-r from-transparent via-[#C8A84B]/45 to-transparent transition-all duration-500 group-hover:via-[#C8A84B]/72"/>
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-2 py-5">
        <div className="mb-3 transition-all duration-500 group-hover:scale-[1.06]" style={{ color: isActive ? '#C9A227' : '#E2BF50' }}>{icon}</div>
        <span className="text-center uppercase leading-tight transition-all duration-500"
          style={{ fontFamily:'Cinzel, serif', fontSize:'clamp(0.72rem, 3.4vw, 0.95rem)', letterSpacing:'0.04em', fontWeight:700, color:'#1C1608' }}>{label}</span>
        <div className="mt-3 flex items-center gap-3 opacity-70 transition-all duration-500 group-hover:opacity-100">
          <div className="h-px w-6 transition-all duration-500" style={{ background: isActive ? 'rgba(180,140,30,0.60)' : 'rgba(180,140,30,0.38)' }}/>
          <div className="h-[4px] w-[4px] rounded-full transition-all duration-500" style={{ background: isActive ? '#B8920A' : 'rgba(180,140,30,0.65)' }}/>
          <div className="h-px w-6 transition-all duration-500" style={{ background: isActive ? 'rgba(180,140,30,0.60)' : 'rgba(180,140,30,0.38)' }}/>
        </div>
      </div>
    </button>
  );
}

function MenuTiles({ activeMenu, setActiveMenu, menuData }) {
  const [isClosing, setIsClosing] = React.useState(false);
  const [displayed, setDisplayed] = React.useState(null);
  React.useEffect(() => {
    if (activeMenu) { setIsClosing(false); setDisplayed(activeMenu); }
  }, [activeMenu]);
  const close = () => {
    setIsClosing(true);
    setTimeout(() => { setActiveMenu(null); setDisplayed(null); setIsClosing(false); }, 650);
  };
  const shown = displayed || activeMenu;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {['Appetizer','Main Course','Dessert'].map(cat => (
          <MenuTile key={cat} label={cat} isActive={activeMenu === cat}
            onClick={() => setActiveMenu(activeMenu === cat ? null : cat)} icon={<Utensils size={28}/>}/>
        ))}
      </div>
      {shown && (
        <div className={`mt-6 ${isClosing ? 'animate-out slide-out-to-bottom-8 fade-out duration-700' : 'animate-in slide-in-from-bottom-8 fade-in duration-700'}`}>
          <div className="border border-[#D4AF37]/45 rounded-[1.8rem] px-6 py-6 relative overflow-hidden"
            style={{ background:'rgba(255,252,240,0.96)', boxShadow:'0 24px 50px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.90)' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.08] bg-[radial-gradient(circle_at_top,#D4AF37,transparent_45%)]"/>
            <button onClick={close} className="absolute top-4 right-4 text-[#C19A35] hover:scale-110 transition-transform">
              <X className="w-5 h-5"/>
            </button>
            <div className="relative text-center mb-6">
              {shown === 'Main Course' && (
                <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'13px', fontStyle:'italic', fontWeight:500, color:'#B8962E', marginBottom:8 }}>Menu Selection</p>
              )}
              <h4 className="text-[1.9rem] leading-none text-[#1E1A17]" style={{ fontFamily:'Cormorant Garamond,serif' }}>
                {shown === 'Appetizer' ? 'Appetizers' : shown === 'Main Course' ? 'Main Course' : 'Desserts'}
              </h4>
              <div className="flex justify-center items-center gap-3 mt-4">
                <div className="w-8 h-px bg-[#D4AF37]/35"/><span className="text-[#D4AF37] text-[8px]">✦</span><div className="w-8 h-px bg-[#D4AF37]/35"/>
              </div>
            </div>
            <ul className="relative max-w-sm mx-auto space-y-4">
              {menuData[shown].map((item, i) => (
                <li key={i} className="text-center">
                  <p className="text-[1.45rem] leading-snug text-[#2B241D]" style={{ fontFamily:'Cormorant Garamond,serif' }}>{item}</p>
                  {i < menuData[shown].length - 1 && (
                    <div className="mt-3 flex justify-center"><div className="flex items-center gap-3 opacity-40"><div className="w-7 h-px bg-[#D4AF37]"/><span className="text-[#D4AF37] text-[8px]">✦</span><div className="w-7 h-px bg-[#D4AF37]"/></div></div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Requirement 6 + 7: Individual guest card ─────────────────────────────────
function GuestCard({
  guest, index, token,
  guestType, onTypeChange,
  dietary, onDietaryChange,
  specialReq, onSpecialReqChange,
  showValidation,
  onHasEdits,
  onEmailSaved,
  // Carter fix (Feb 2026) — per-guest YES/NO. Both undefined = solo invite,
  // toggle is hidden and behavior is unchanged from the pre-fix module.
  decision,
  onDecisionChange,
  showDecisionToggle,
}) {
  const [savedName,  setSavedName]  = useState(`${guest.first_name||''} ${guest.last_name||''}`.trim());
  const [savedPhone, setSavedPhone] = useState(guest.phone  || '');
  const [savedEmail, setSavedEmail] = useState(guest.email  || '');

  const origName  = savedName;
  const origPhone = savedPhone;
  const origEmail = savedEmail;

  const [editing,    setEditing]    = useState(false);
  const [editField,  setEditField]  = useState(null); // 'phone' or 'email' for inline edit
  const [editName,   setEditName]   = useState(origName);
  const [editPhone,  setEditPhone]  = useState(origPhone);
  const [editEmail,  setEditEmail]  = useState(origEmail);
  const [showDiet,   setShowDiet]   = useState(false);
  const [showSpec,   setShowSpec]   = useState(false);
  const [saving,     setSaving]     = useState(false);

  const hasEdits = editName !== origName || editPhone !== origPhone || editEmail !== origEmail;

  // Notify parent when edit state changes
  useEffect(() => { onHasEdits(guest.id, hasEdits); }, [hasEdits]);

  const handleSaveContact = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const nameParts = editName.trim().split(/\s+/);
      await fetch(`${API}/api/rsvp/${token}/guest/${guest.id}/contact`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: nameParts[0] || '',
          last_name:  nameParts.slice(1).join(' ') || '',
          phone:      editPhone,
          email:      editEmail,
        }),
      });
      // Update local saved state so view mode reflects the new values
      setSavedName(editName.trim());
      setSavedPhone(editPhone);
      setSavedEmail(editEmail);
      // Notify parent if email was provided
      if (editEmail.trim() && onEmailSaved) onEmailSaved(editEmail.trim());
      setEditing(false);
      setEditField(null);
    } catch { /* surface error if needed */ }
    setSaving(false);
  };

  const dietLabels = DIETARY_OPTIONS.filter(d => dietary.includes(d.key));
  const typeError  = showValidation && !guestType;

  return (
    <div style={{
      borderBottom: '1px solid rgba(175,148,230,0.18)',
      borderLeft:   '3px solid rgba(198,167,90,0.55)',
      paddingLeft: 12, paddingBottom: 16, marginBottom: 16,
    }}>
      {/* Requirement 6 TOP: Gold label "Guest X" — now flex with optional inline AttendanceToggle on right */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        margin: '0 0 6px',
        minHeight: 22,
      }}>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic',
          fontSize:13, fontWeight:500, letterSpacing:'0.8px',
          color:'rgba(212,185,100,0.90)', margin: 0 }}>
          Guest {index + 1}
        </p>
        {showDecisionToggle && (
          <AttendanceToggle decision={decision} onChange={onDecisionChange} />
        )}
      </div>

      {editing ? (
        /* ─── Edit mode ─── */
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
          <input value={editName}  onChange={e => setEditName(e.target.value)}
            placeholder="Full name"
            style={inputStyle} />
          <input value={editPhone} onChange={e => setEditPhone(e.target.value)}
            placeholder="Phone number"
            style={inputStyle} />
          <input value={editEmail} onChange={e => setEditEmail(e.target.value)}
            placeholder="Email address" type="email"
            style={inputStyle} />
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handleSaveContact} disabled={saving} style={smallBtnStyle('#1E7344')}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setEditName(origName); setEditPhone(origPhone); setEditEmail(origEmail); }}
              style={smallBtnStyle('rgba(150,90,90,0.80)')}>Cancel</button>
          </div>
        </div>
      ) : (
        /* ─── View mode — 2-column grid layout ─── */
        <div style={{ marginBottom: 10 }}>

          {/* Row 1: Guest name — full width */}
          <p style={{ fontFamily:"'Cinzel',serif", fontSize:15, fontWeight:600, margin:'0 0 4px',
            letterSpacing:'0.3px', color:WHITE97,
            textShadow:'0 1px 1px rgba(0,0,0,0.25)' }}>
            {origName || <span style={{ color:GOLD_DIM, fontStyle:'italic' }}>Name not provided</span>}
          </p>

          {/* Diet tags — full width */}
          {dietLabels.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:4, margin:'4px 0 6px' }}>
              {dietLabels.map(d => (
                <span key={d.key} style={{ fontSize:9, padding:'2px 7px', borderRadius:99,
                  background:'rgba(198,167,90,0.18)', border:'1px solid rgba(198,167,90,0.40)',
                  color:GOLD, fontFamily:"'Cinzel',serif", letterSpacing:'0.5px', textTransform:'uppercase' }}>
                  {d.icon} {d.label}
                </span>
              ))}
            </div>
          )}

          {/* Saved special request display — full width */}
          {specialReq && (
            <p style={{ fontSize:11, color:WHITE70, fontStyle:'italic', margin:'0 0 6px', lineHeight:1.5 }}>
              "{specialReq}"
            </p>
          )}

          {/* Rows 2–4: 2-column grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px 10px',
            alignItems: 'start',
            marginTop: 8,
          }}>
            {/* Row 2 Left: Phone — tappable to edit when "Not Provided" */}
            {editField === 'phone' ? (
              <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                <input value={editPhone} onChange={e => setEditPhone(e.target.value)}
                  placeholder="Phone number" autoFocus
                  style={{ ...inputStyle, padding:'6px 8px', fontSize:12, flex:1 }} />
                <button onClick={handleSaveContact} disabled={saving}
                  style={{ ...smallBtnStyle('#1E7344'), padding:'5px 8px', fontSize:9 }}>
                  {saving ? '...' : 'Save'}
                </button>
                <button onClick={() => { setEditField(null); setEditPhone(origPhone); }}
                  style={{ ...smallBtnStyle('rgba(150,90,90,0.80)'), padding:'5px 6px', fontSize:9 }}>X</button>
              </div>
            ) : (
              <div onClick={() => { if (!origPhone) setEditField('phone'); }}
                data-testid="guest-phone-display"
                style={{ height: 42, padding: '0 12px', borderRadius: 8, boxSizing: 'border-box',
                  border: '1.5px solid rgba(212,168,83,0.55)', background: 'rgba(255,255,255,0.95)',
                  display: 'flex', alignItems: 'center', gap: 6,
                  cursor: origPhone ? 'default' : 'pointer', overflow: 'hidden' }}>
                <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 13, fontWeight: 600,
                  color: '#1A1A1A', whiteSpace: 'nowrap', flexShrink: 0 }}>Phone:</span>
                <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 13,
                  color: '#555555', fontStyle: origPhone ? 'normal' : 'italic',
                  textDecoration: origPhone ? 'none' : 'underline',
                  textDecorationColor: 'rgba(0,0,0,0.30)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {origPhone || 'Not Provided'}
                </span>
              </div>
            )}
            {/* Row 2 Right: Dietary toggle */}
            <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }}>
              <GoldCheckbox checked={showDiet} onChange={() => setShowDiet(v => !v)}/>
              <span style={{ fontSize:11, fontWeight:500, color:WHITE97, whiteSpace:'nowrap' }}>
                Dietary Restrictions
              </span>
            </label>

            {/* Row 3 Left: Email — tappable to edit when "Not Provided" */}
            {editField === 'email' ? (
              <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                <input value={editEmail} onChange={e => setEditEmail(e.target.value)}
                  placeholder="Email address" type="email" autoFocus
                  style={{ ...inputStyle, padding:'6px 8px', fontSize:12, flex:1 }} />
                <button onClick={handleSaveContact} disabled={saving}
                  style={{ ...smallBtnStyle('#1E7344'), padding:'5px 8px', fontSize:9 }}>
                  {saving ? '...' : 'Save'}
                </button>
                <button onClick={() => { setEditField(null); setEditEmail(origEmail); }}
                  style={{ ...smallBtnStyle('rgba(150,90,90,0.80)'), padding:'5px 6px', fontSize:9 }}>X</button>
              </div>
            ) : (
              <div onClick={() => { if (!origEmail) setEditField('email'); }}
                data-testid="guest-email-display"
                style={{ height: 42, padding: '0 12px', borderRadius: 8, boxSizing: 'border-box',
                  border: '1.5px solid rgba(212,168,83,0.55)', background: 'rgba(255,255,255,0.95)',
                  display: 'flex', alignItems: 'center', gap: 6,
                  cursor: origEmail ? 'default' : 'pointer', overflow: 'hidden' }}>
                <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 13, fontWeight: 600,
                  color: '#1A1A1A', whiteSpace: 'nowrap', flexShrink: 0 }}>Email:</span>
                <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 13,
                  color: '#555555', fontStyle: origEmail ? 'normal' : 'italic',
                  textDecoration: origEmail ? 'none' : 'underline',
                  textDecorationColor: 'rgba(0,0,0,0.30)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {origEmail || 'Not Provided'}
                </span>
                {!origEmail && guestType !== 'child' && (
                  <span style={{ color:'#FF4444', fontSize:14, fontWeight:700, marginLeft:'auto', flexShrink: 0 }}>*</span>
                )}
              </div>
            )}
            {/* Row 3 Right: Special Request toggle */}
            <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }}>
              <GoldCheckbox checked={showSpec} onChange={() => setShowSpec(v => !v)}/>
              <span style={{ fontSize:11, fontWeight:500, color:WHITE97, whiteSpace:'nowrap' }}>
                Special Request
              </span>
            </label>

            {/* Row 4 Left: required indicator */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end' }}>
              <span style={{ color:'#FF4444', fontSize:14, fontWeight:700 }}>*</span>
            </div>
            {/* Row 4 Right: Adult/Child selector */}
            <div>
              <GuestTypeSelector value={guestType} onChange={onTypeChange} hasError={typeError}/>
              {typeError && (
                <p style={{ fontSize:10, color:'#FF9090', margin:'6px 0 0', fontStyle:'italic' }}>
                  Please select Adult or Child for each guest.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dietary expanded — full width, below grid */}
      <div style={{ overflow:'hidden', maxHeight: showDiet ? '220px' : '0px', transition:'max-height 0.30s ease' }}>
        <div style={{ paddingTop:8, overflowX:'auto', scrollbarWidth:'none' }}>
          <div style={{ display:'flex', gap:5, minWidth:'max-content', paddingBottom:2 }}>
            {DIETARY_OPTIONS.map(d => {
              const active = dietary.includes(d.key);
              return (
                <button key={d.key}
                  onClick={() => onDietaryChange(d.key)}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center', gap:3,
                    padding:'7px 8px', borderRadius:10, cursor:'pointer',
                    background: active ? 'rgba(198,167,90,0.22)' : 'rgba(255,255,255,0.08)',
                    border: active ? '1.5px solid rgba(198,167,90,0.55)' : '1px solid rgba(198,167,90,0.22)',
                    transition:'all 0.14s ease', outline:'none',
                  }}>
                  <span style={{ fontSize:14 }}>{d.icon}</span>
                  <span style={{ fontSize:8, fontWeight:600, color:WHITE97, whiteSpace:'nowrap' }}>{d.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Special request textarea — full width, below grid */}
      <style>{`.rsvp-spec-req::placeholder { color: rgba(255,255,255,0.55) !important; }`}</style>
      <div style={{ overflow:'hidden', maxHeight: showSpec ? '120px' : '0px', transition:'max-height 0.30s ease' }}>
        <textarea
          className="rsvp-spec-req"
          value={specialReq}
          onChange={e => onSpecialReqChange(e.target.value)}
          placeholder="Need wheelchair access, etc."
          rows={2}
          style={{
            marginTop:8, width:'100%', padding:'10px 12px', boxSizing:'border-box',
            borderRadius:10, resize:'none',
            border:'1.5px solid rgba(198,167,90,0.26)',
            background:'rgba(255,255,255,0.08)',
            boxShadow:'inset 0 1px 0 rgba(255,255,255,0.65)',
            fontSize:12, color:WHITE97, fontFamily:'inherit', outline:'none', lineHeight:1.5,
          }}
        />
      </div>
    </div>
  );
}

// Shared input style for edit mode
const inputStyle = {
  width:'100%', padding:'9px 12px', borderRadius:8, boxSizing:'border-box',
  border:'1.5px solid rgba(198,167,90,0.35)',
  background:'rgba(255,255,255,0.10)',
  fontSize:13, color:WHITE97, fontFamily:'inherit', outline:'none',
};
const smallBtnStyle = (bg) => ({
  padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer',
  background:bg, color:'#fff', fontSize:11, fontWeight:700, fontFamily:'inherit',
});

// ─── Gold divider ─────────────────────────────────────────────────────────────
const GoldDiv = () => (
  <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'center' }}>
    <div style={{ width:36, height:1, background:'linear-gradient(90deg,transparent,rgba(212,185,80,0.90))' }}/>
    <span style={{ color:'rgba(212,185,80,1)', fontSize:11 }}>✦</span>
    <div style={{ width:36, height:1, background:'linear-gradient(90deg,rgba(212,185,80,0.90),transparent)' }}/>
  </div>
);

// ─── One-word component (unchanged) ───────────────────────────────────────────
function DescribeCarol({ primaryGuest, token }) {
  const [word,      setWord]      = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending,   setSending]   = useState(false);
  const [errorMsg,  setErrorMsg]  = useState('');
  const submit = async () => {
    if (!word.trim() || sending) return;
    setErrorMsg('');
    setSending(true);
    try {
      const res = await fetch(`${API}/api/messages`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          guest_name: primaryGuest?.first_name || 'Guest',
          last_name: primaryGuest?.last_name || '',
          email: primaryGuest?.email || '',
          guest_id: primaryGuest?.id || primaryGuest?._id || null,
          content: `[Describe Carol in 1 word]: ${word.trim()}`,
        }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      setSubmitted(true);
    } catch (e) {
      setErrorMsg("Sorry — that didn't go through. Please try again.");
    } finally {
      setSending(false);
    }
  };
  if (submitted) return (
    <div style={{ textAlign:'center', padding:'8px 0' }}>
      <p style={{ fontFamily:"'Cinzel',serif", fontSize:12, fontWeight:700,
        color:'rgba(212,175,55,0.85)', margin:'0 0 8px', letterSpacing:'1.5px' }}>✦ &nbsp;Thank you for sharing</p>
      <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontStyle:'italic',
        color:WHITE97, margin:0 }}>"{word}"</p>
    </div>
  );
  return (
    <div style={{ width:'100%' }}>
      <GoldDiv />
      <div style={{ margin:'20px 0' }}>
        <p style={{ fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:700,
          color:WHITE97, textAlign:'center', margin:'0 0 4px', letterSpacing:'0.5px' }}>Describe Carol</p>
        <p style={{ fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:700,
          color:WHITE97, textAlign:'center', margin:'0 0 20px', letterSpacing:'0.5px' }}>in 1 word.</p>
        <input value={word} onChange={e => setWord(e.target.value.slice(0,30))}
          onKeyDown={e => e.key==='Enter' && submit()} placeholder="e.g. Radiant" maxLength={30}
          disabled={sending}
          style={{ width:'100%', height:54, padding:'0 18px', boxSizing:'border-box', borderRadius:14,
            border:'1.5px solid rgba(212,175,55,0.45)', background:'rgba(255,255,255,0.10)',
            backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
            fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic',
            fontSize:20, color:WHITE97, outline:'none', textAlign:'center', letterSpacing:'1px',
            marginBottom:12, display:'block',
          }}/>
        <button onClick={submit} disabled={sending} style={{ width:'100%', height:50, borderRadius:12, border:'1px solid rgba(200,170,60,0.65)', cursor: sending ? 'default' : 'pointer',
          background:'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)',
          boxShadow:'0 4px 16px rgba(0,0,0,0.18), inset 0 1px 0 rgba(212,168,83,0.15)',
          fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:700,
          color:'#D4A853', letterSpacing:'2.5px', textTransform:'uppercase',
          textShadow:'0 1px 3px rgba(100,60,0,0.25)', opacity: sending ? 0.6 : 1 }}>
          {sending ? 'SENDING…' : 'SEND'}
        </button>
        {errorMsg && (
          <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, fontStyle:'italic',
            color:'rgba(255,200,200,0.95)', textAlign:'center', margin:'10px 0 0' }}>
            {errorMsg}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main RSVP Card ────────────────────────────────────────────────────────────
function RSVPCard({ party, guests, token, menuData, guestMode = 'unrestricted', editMode = false, attribution = null }) {
  const activeGuests = guests && guests.length > 0 ? guests : [];

  // Per-guest state (Requirement 10: dynamic, not hardcoded)
  const [guestTypes,      setGuestTypes]      = useState({});
  const [dietarySelections, setDietarySelections] = useState({});
  const [specialRequests, setSpecialRequests] = useState({});
  const [guestEditFlags, setGuestEditFlags] = useState({});
  // Carter fix (Feb 2026): per-guest RSVP decision so a grouped household
  // can submit mixed responses ("Elaine YES, David NO, Brooklyn YES") in a
  // single submission. Default seeded to 'YES' (we assume most guests are
  // attending). Edit-mode hydration below pre-fills from each guest's
  // existing rsvp_status.
  const [guestDecisions, setGuestDecisions] = useState({});

  // Sync guest data when API response arrives
  useEffect(() => {
    if (activeGuests.length === 0) return;
    setGuestTypes(prev => {
      const m = { ...prev };
      activeGuests.forEach(g => {
        if (m[g.id] === undefined) {
          m[g.id] = g.age_group === 'ADULT' ? 'adult' : g.age_group === 'CHILD' ? 'child' : null;
        }
      });
      return m;
    });
    setDietarySelections(prev => {
      const m = { ...prev };
      activeGuests.forEach(g => {
        if (m[g.id] === undefined) {
          const reqs = Array.isArray(g.dietary_requirements) ? g.dietary_requirements : [];
          m[g.id] = reqs.filter(r => DIETARY_OPTIONS.some(d => d.key === r));
        }
      });
      return m;
    });
    setSpecialRequests(prev => {
      const m = { ...prev };
      activeGuests.forEach(g => {
        if (m[g.id] === undefined) m[g.id] = g.other_notes || '';
      });
      return m;
    });
    // Default decision = YES per guest. Edit-mode hydration runs separately
    // below and overrides this with each guest's persisted rsvp_status.
    setGuestDecisions(prev => {
      const m = { ...prev };
      activeGuests.forEach(g => {
        if (m[g.id] === undefined) m[g.id] = 'YES';
      });
      return m;
    });
  }, [activeGuests.length]);

  const [rsvpDecision,  setRsvpDecision]  = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [activeMenu,    setActiveMenu]    = useState(null);
  const [submitted,     setSubmitted]     = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState('');
  const [editAlert,     setEditAlert]     = useState(false);
  const [showBalloons,  setShowBalloons]  = useState(false);
  const [requestedAdditional, setRequestedAdditional] = useState(0);
  const [additionalGuests, setAdditionalGuests] = useState([]);
  // Grouped-edit attribution: who is currently acting on this shared RSVP.
  const [actingGuestId, setActingGuestId] = useState(null);

  // Hydrate grouped-edit state from prior submission, once.
  useEffect(() => {
    if (!editMode) return;
    if (!activeGuests.length) return;
    // Pre-fill RSVP decision from guest statuses.
    const yesCount = activeGuests.filter(g => (g.rsvp_status || '').toUpperCase() === 'YES').length;
    const noCount  = activeGuests.filter(g => (g.rsvp_status || '').toUpperCase() === 'NO').length;
    if (yesCount > 0) setRsvpDecision(prev => prev || 'accept');
    else if (noCount === activeGuests.length && noCount > 0) setRsvpDecision(prev => prev || 'decline');
    // Carter fix: pre-fill EACH guest's per-person decision from their stored
    // rsvp_status. Mixed-status households now correctly re-hydrate their
    // toggles instead of being aggregated under a single decision.
    setGuestDecisions(prev => {
      const m = { ...prev };
      activeGuests.forEach(g => {
        const s = (g.rsvp_status || '').toUpperCase();
        if (s === 'YES' || s === 'NO') m[g.id] = s;
      });
      return m;
    });
    // Pre-fill additional guests from party logistics.
    const logistics = (party && party.rsvp_logistics) || {};
    const names = Array.isArray(logistics.additional_guest_names) ? logistics.additional_guest_names : [];
    if (names.length > 0) {
      setAdditionalGuests(prev => prev.length > 0 ? prev : names.map(n => ({
        first_name: n.first_name || '',
        last_name: n.last_name || '',
        is_adult: n.is_adult !== false,
      })));
    }
    // Pre-fill requested_additional_guests (restricted mode).
    if ((logistics.requested_additional_guests || 0) > 0) {
      setRequestedAdditional(prev => prev || logistics.requested_additional_guests);
    }
    // Default actingGuestId to the primary guest.
    const primary = activeGuests.find(g => g.is_primary) || activeGuests[0];
    setActingGuestId(prev => prev || (primary && primary.id));
  }, [editMode, activeGuests.length, party]);

  // Email requirement: check if any guest already has email on file
  const existingEmail = activeGuests.find(g => g.email && g.email.trim())?.email?.trim() || '';
  const [respondentEmail, setRespondentEmail] = useState(existingEmail);
  const [emailError, setEmailError] = useState('');
  const hasEmailOnFile = !!existingEmail;

  // Requirement 7: Any guest has edits?
  const hasAnyEdits = Object.values(guestEditFlags).some(Boolean);

  const toggleDietary = (guestId, key) => {
    setDietarySelections(prev => ({
      ...prev,
      [guestId]: prev[guestId]?.includes(key)
        ? prev[guestId].filter(k => k !== key)
        : [...(prev[guestId] || []), key],
    }));
  };

  const handleSubmit = async () => {
    // ISSUE #2 fix: With no valid token there is no invitation to write to.
    // Block submission entirely (no network POST, no false success screen) and
    // direct the guest to their personalized invitation link. Viewing is allowed.
    if (!token) {
      setError('Please use your personalized invitation link to RSVP — check your invitation email or text for your link.');
      return;
    }
    // Only validate email if there are actual guests (token resolved)
    if (activeGuests.length > 0 && !hasEmailOnFile) {
      const emailVal = respondentEmail.trim();
      if (!emailVal) {
        setEmailError('Please enter a valid email address');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      setEmailError('');
    }
    // Carter fix: build the per-guest status payload and validation list.
    // - Accept mode  → each guest's status comes from guestDecisions[g.id]
    //                  (default 'YES'; user can flip individuals to 'NO').
    // - Decline mode → every guest is 'NO'. (Top "Sadly, No" already seeded
    //                  guestDecisions to 'NO' for everyone, but we force it
    //                  here as a belt-and-braces safeguard.)
    const perGuestStatuses = activeGuests.map(g => ({
      id: g.id,
      status: rsvpDecision === 'decline' ? 'NO' : (guestDecisions[g.id] || 'YES'),
    }));
    const yesGuestIds = new Set(perGuestStatuses.filter(x => x.status === 'YES').map(x => x.id));

    // Requirement 13 (Carter-fix scope): Adult/Child validation runs ONLY for
    // guests marked Attending. Decliners do not need an age selection.
    if (rsvpDecision === 'accept') {
      const missing = activeGuests.find(g => yesGuestIds.has(g.id) && !guestTypes[g.id]);
      if (missing) { setShowValidation(true); return; }
    }
    setSubmitting(true);
    setError('');
    try {
      if (token) {
        const yesAdults = activeGuests.filter(g => yesGuestIds.has(g.id) && guestTypes[g.id] === 'adult').length;
        const yesChildren = activeGuests.filter(g => yesGuestIds.has(g.id) && guestTypes[g.id] === 'child').length;
        const res = await fetch(`${API}/api/rsvp/${token}`, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({
            guests: activeGuests.map(g => {
              const status = perGuestStatuses.find(x => x.id === g.id)?.status || 'NO';
              return {
                guest_id: g.id,
                rsvp_status: status,
                dietary_requirements: (dietarySelections[g.id] || []).join(', '),
                other_notes: specialRequests[g.id] || '',
              };
            }),
            logistics: {
              total_attending: yesGuestIds.size,
              adults_count: yesAdults,
              children_count: yesChildren,
              requested_additional_guests: guestMode === 'restricted' ? requestedAdditional : additionalGuests.length,
              additional_guest_names: guestMode === 'unrestricted' ? additionalGuests.filter(g => g.first_name.trim()) : [],
              respondent_email: !hasEmailOnFile ? respondentEmail.trim() : undefined,
            },
            acting_guest_id: actingGuestId || undefined,
          }),
        });
        if (!res.ok) {
          let detail = '';
          try { detail = (await res.json())?.detail || ''; } catch {}
          setError(res.status === 409
            ? (detail || 'This invitation has already been submitted. Please contact the hosts to make a change.')
            : (detail || 'We could not record your RSVP. Please try again, or contact the hosts.'));
          setSubmitting(false);
          return;
        }
      }
      if (hasAnyEdits) setEditAlert(true);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  // ── Submitted state ──
  if (submitted) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      padding:'40px 0', minHeight:'60vh' }}>
      <div style={{ width:50, height:50, borderRadius:'50%',
        background:'linear-gradient(145deg,rgba(235,255,242,0.96),rgba(200,240,218,0.86))',
        border:'1.5px solid rgba(34,160,90,0.28)',
        boxShadow:'0 2px 14px rgba(34,160,90,0.16),inset 0 1px 0 rgba(255,255,255,0.80)',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:22, color:'#2D7A4F', marginBottom:12 }}>✓</div>
      <p style={{ fontFamily:"'Cinzel',serif", fontSize:22, fontWeight:700,
        color:WHITE97, margin:'0 0 6px', textAlign:'center', letterSpacing:'1.5px' }}>RSVP Submitted</p>
      {editAlert && (
        <p style={{ fontSize:11, color:'rgba(212,185,80,0.90)', textAlign:'center',
          margin:'0 0 12px', fontStyle:'italic', border:'1px solid rgba(212,185,80,0.40)',
          borderRadius:8, padding:'6px 12px', background:'rgba(212,185,80,0.10)' }}>
          Guest contact information updated
        </p>
      )}
      <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontSize:20,
        color:WHITE97, textAlign:'center', lineHeight:1.7, margin:'0 0 6px' }}>
        Thank you{activeGuests[0] ? `, ${activeGuests[0].first_name}` : ''}.<br/>
        We look forward to celebrating with you.
      </p>
      {requestedAdditional > 0 && (
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontSize:14,
          color:'rgba(212,185,80,0.90)', textAlign:'center', lineHeight:1.7, margin:'0 0 28px' }}>
          Your request to add an additional guest has been sent to the host.<br/>
          We'll follow up with you as soon as possible.
        </p>
      )}
      {requestedAdditional === 0 && <div style={{ marginBottom:28 }} />}
      <DescribeCarol primaryGuest={activeGuests[0]} token={token}/>
    </div>
  );

  const displayName = resolveDisplayName(guests, party?.party_name);

  return (
    <div>
      <BalloonBurst active={showBalloons} />
      {/* ── Header: Welcome → divider → name (if token) → tagline → divider ── */}
      <div id="rsvp-welcome-header" style={{ textAlign:'center', marginBottom:20 }}>

        {/* "Welcome" — always visible, no top divider */}
        <p style={{
          fontFamily:"'Cinzel', serif",
          fontSize:38, fontWeight:600, lineHeight:1,
          margin:'0 0 12px',
          color:'#B8902A',
          letterSpacing:'3px',
          textTransform:'uppercase',
          textShadow:'0 2px 0 rgba(255,255,255,0.70), 0 -1px 0 rgba(80,50,0,0.15)',
        }}>
          Welcome
        </p>

        {/* Divider 1 — always below Welcome */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
          <div style={{ flex:1, height:1, background:'linear-gradient(90deg, transparent, rgba(212,185,80,0.80))' }} />
          <span style={{ color:'rgba(212,185,80,0.95)', fontSize:11 }}>✦</span>
          <div style={{ flex:1, height:1, background:'linear-gradient(90deg, rgba(212,185,80,0.80), transparent)' }} />
        </div>

        {/* Resolved name — only when token resolves a name */}
        {displayName && (
          <p style={{
            fontFamily:"'Cinzel',serif", fontSize:30, fontWeight:700,
            color:WHITE97, margin:'0 0 10px',
            letterSpacing:'3px', textTransform:'uppercase',
            textShadow:'0 1px 0 rgba(255,255,255,0.40), 0 1px 8px rgba(0,0,0,0.25)',
          }}>
            {displayName}
          </p>
        )}

        {/* Tagline — always visible */}
        <p style={{
          fontFamily:"'Cormorant Garamond', serif", fontSize:30, fontStyle:'italic',
          color:WHITE97, margin:'0 0 12px', lineHeight:1.5,
        }}>
          Will you help write Carol's next chapter?
        </p>

        {/* Divider 2 — always below tagline */}
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ flex:1, height:1, background:'linear-gradient(90deg, transparent, rgba(212,185,80,0.80))' }} />
          <span style={{ color:'rgba(212,185,80,0.95)', fontSize:11 }}>✦</span>
          <div style={{ flex:1, height:1, background:'linear-gradient(90deg, rgba(212,185,80,0.80), transparent)' }} />
        </div>
      </div>

      {/* Grouped-edit attribution strip + Submitting-as pill (only when editing a shared RSVP) */}
      {editMode && attribution && attribution.submitted_at && (
        <div data-testid="rsvp-attribution-strip" style={{
          marginBottom: 14, padding: '12px 16px', borderRadius: 12,
          background: 'rgba(0,0,0,0.45)',
          border: '1px solid rgba(212,168,83,0.45)',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif",
            fontSize: 15, color: '#FFFFFF', lineHeight: 1.5, fontWeight: 500 }}>
            Submitted by <span style={{ fontWeight: 700, color: '#FFE9A8' }}>{attribution.submitted_by_name || 'a group member'}</span>
            {attribution.submitted_at ? ` · ${new Date(attribution.submitted_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
          </div>
          {(attribution.last_updated_at && (attribution.last_updated_at !== attribution.submitted_at || attribution.last_updated_by_guest_id !== attribution.submitted_by_guest_id)) && (
            <div style={{ fontFamily: "'Cormorant Garamond',serif",
              fontSize: 14, color: '#FFFFFF', lineHeight: 1.5, opacity: 0.92 }}>
              Last updated by <span style={{ fontWeight: 700, color: '#FFE9A8' }}>{attribution.last_updated_by_name || 'a group member'}</span>
              {attribution.last_updated_at ? ` · ${new Date(attribution.last_updated_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
            </div>
          )}
          {activeGuests.length >= 2 && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#FFFFFF',
              display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Cinzel',serif", letterSpacing: '0.5px', fontWeight: 600 }}>
              <span style={{ textTransform: 'uppercase' }}>Updating as</span>
              <select data-testid="acting-guest-select"
                value={actingGuestId || ''}
                onChange={e => setActingGuestId(e.target.value)}
                style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: 'rgba(0,0,0,0.6)', color: '#FFE9A8',
                  border: '1px solid rgba(212,168,83,0.55)', fontFamily: 'inherit' }}>
                {activeGuests.map(g => (
                  <option key={g.id} value={g.id} style={{ color: '#111', background: '#FFF' }}>
                    {`${g.first_name || ''} ${g.last_name || ''}`.trim() || 'Guest'}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* ── RSVP buttons — always visible ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
        {/* Joyfully Accept */}
        <button onClick={() => {
          setRsvpDecision('accept');
          setShowBalloons(true);
          // Carter fix: top "Yes" tap is a non-conflicting shortcut. It seeds
          // every guest's per-person decision to YES. Real submission still
          // reads from per-guest guestDecisions, so individuals can be flipped
          // afterward.
          setGuestDecisions(prev => {
            const m = { ...prev };
            activeGuests.forEach(g => { m[g.id] = 'YES'; });
            return m;
          });
        }} style={{
          padding:'15px 10px', borderRadius:12, border:'none', cursor:'pointer', outline:'none',
          display:'flex', alignItems:'center', justifyContent:'center',
          background: rsvpDecision === 'accept' ? GREEN_BG : 'rgba(255,255,255,0.12)',
          boxShadow: rsvpDecision === 'accept' ? GREEN_GLOW : '0 0 10px rgba(30,140,78,0.10),inset 0 1px 0 rgba(255,255,255,0.15),0 0 0 1px rgba(30,140,78,0.25)',
          transition:'all 0.20s ease',
        }}>
          <span style={{ fontFamily:"'Cinzel',serif", fontSize:14, fontWeight:600,
            letterSpacing:'0.8px',
            color: rsvpDecision === 'accept' ? '#FFFFFF' : 'rgba(80,200,100,0.95)',
            textAlign:'center', lineHeight:1.4 }}>
            Yes, I'll<br/>be there!
          </span>
        </button>

        {/* Requirement 1: DECLINE */}
        <button onClick={() => {
          setRsvpDecision('decline');
          // Carter fix: top "Sadly, No" seeds every guest to NO.
          setGuestDecisions(prev => {
            const m = { ...prev };
            activeGuests.forEach(g => { m[g.id] = 'NO'; });
            return m;
          });
        }} style={{
          padding:'15px 10px', borderRadius:12, border:'none', cursor:'pointer', outline:'none',
          display:'flex', alignItems:'center', justifyContent:'center',
          background: rsvpDecision === 'decline' ? RED_BG : 'rgba(255,255,255,0.12)',
          boxShadow: rsvpDecision === 'decline' ? RED_GLOW : '0 0 10px rgba(165,70,70,0.08),inset 0 1px 0 rgba(255,255,255,0.15),0 0 0 1px rgba(165,70,70,0.22)',
          transition:'all 0.20s ease',
        }}>
          <span style={{ fontFamily:"'Cinzel',serif", fontSize:13, fontWeight:600,
            letterSpacing:'0.6px',
            color: rsvpDecision === 'decline' ? '#FFFFFF' : 'rgba(255,255,255,0.90)',
            lineHeight:1.4 }}>
            Sadly, No
          </span>
        </button>
      </div>

      {!rsvpDecision && (
        <p style={{ fontSize:12, color:WHITE97, textAlign:'center', fontStyle:'italic', padding:'4px 0 8px', margin:0 }}>
          Please make your selection above
        </p>
      )}

      {/* Requirement 2: Smooth ease-in-out expansion for Accept */}
      <div style={{
        overflow: 'hidden',
        maxHeight:   rsvpDecision === 'accept' ? '4000px' : '0px',
        opacity:     rsvpDecision === 'accept' ? 1 : 0,
        transition: 'max-height 1.4s ease-in-out, opacity 0.7s ease',
      }}>
        {/* Requirement 5 + 10: Dynamic guest cards from real data, no hardcoded placeholders */}
        {activeGuests.length === 0 ? (
          <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontStyle:'italic',
            color:WHITE97, textAlign:'center', lineHeight:1.7, margin:'8px 0 12px', padding:'0 8px' }}>
            Kindly use your personalized invitation link to RSVP with your guest details.
          </p>
        ) : (
          activeGuests.map((g, i) => (
            <GuestCard
              key={g.id}
              guest={g}
              index={i}
              token={token}
              guestType={guestTypes[g.id] || null}
              onTypeChange={(type) => {
                setGuestTypes(prev => ({ ...prev, [g.id]: type }));
                if (showValidation) setShowValidation(false);
              }}
              dietary={dietarySelections[g.id] || []}
              onDietaryChange={(key) => toggleDietary(g.id, key)}
              specialReq={specialRequests[g.id] || ''}
              onSpecialReqChange={(val) => setSpecialRequests(prev => ({ ...prev, [g.id]: val }))}
              showValidation={showValidation}
              onHasEdits={(id, has) => setGuestEditFlags(prev => ({ ...prev, [id]: has }))}
              onEmailSaved={(email) => { setRespondentEmail(email); setEmailError(''); }}
              decision={guestDecisions[g.id] || 'YES'}
              onDecisionChange={(next) => {
                setGuestDecisions(prev => ({ ...prev, [g.id]: next }));
                if (showValidation && next === 'NO') setShowValidation(false);
              }}
              showDecisionToggle={activeGuests.length >= 2}
            />
          ))
        )}

        {/* Additional Guests — mode-dependent UI */}
        {activeGuests.length > 0 && guestMode === 'restricted' && (
          <div data-testid="additional-guest-request" style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'12px 14px', marginBottom:16,
            borderRadius:12,
            border:'1px solid rgba(198,167,90,0.30)',
            background:'rgba(255,255,255,0.06)',
          }}>
            <span style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic',
              fontSize:14, color:WHITE97, letterSpacing:'0.3px' }}>
              Request Additional Guests
            </span>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <button data-testid="additional-guest-minus" onClick={() => setRequestedAdditional(v => Math.max(0, v - 1))}
                style={{ width:30, height:30, borderRadius:8, border:'1px solid rgba(198,167,90,0.40)',
                  background:'rgba(255,255,255,0.08)', color:WHITE97, fontSize:16, fontWeight:700,
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', outline:'none' }}>
                −
              </button>
              <span data-testid="additional-guest-count" style={{ fontFamily:"'Cinzel',serif", fontSize:18, fontWeight:700,
                color:GOLD, minWidth:24, textAlign:'center' }}>
                {requestedAdditional}
              </span>
              <button data-testid="additional-guest-plus" onClick={() => setRequestedAdditional(v => v + 1)}
                style={{ width:30, height:30, borderRadius:8, border:'1px solid rgba(198,167,90,0.40)',
                  background:'rgba(255,255,255,0.08)', color:WHITE97, fontSize:16, fontWeight:700,
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', outline:'none' }}>
                +
              </button>
            </div>
          </div>
        )}

        {activeGuests.length > 0 && guestMode === 'unrestricted' && (
          <div data-testid="unrestricted-guest-section" style={{ marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', marginBottom:12 }}>
              {additionalGuests.length < 5 && (
                <button data-testid="add-guest-btn" onClick={() => setAdditionalGuests(prev => [...prev, { first_name: '', last_name: '', is_adult: null }])}
                  style={{ padding:'8px 16px', borderRadius:8, border:'1.5px solid rgba(212,168,83,0.85)',
                    background:'rgba(212,168,83,0.15)', color:'#FFE9A8', fontSize:12, fontWeight:700,
                    fontFamily:"'Cinzel',serif", cursor:'pointer', letterSpacing:'0.5px' }}>
                  + Add Guest
                </button>
              )}
            </div>
            {additionalGuests.map((ag, idx) => (
              <div key={idx} data-testid={`additional-guest-${idx}`} style={{
                padding:'14px 16px', marginBottom:10, borderRadius:12,
                border:'1.5px solid rgba(212,168,83,0.45)', background:'rgba(0,0,0,0.35)',
              }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <span style={{ fontFamily:"'Cinzel',serif", fontSize:13, fontWeight:700,
                    color:'#FFE9A8', letterSpacing:'0.8px', textTransform:'uppercase' }}>
                    {additionalGuests.length === 1 ? 'Guest' : `Guest ${idx + 1}`}
                  </span>
                  <button data-testid={`remove-guest-${idx}`}
                    onClick={() => setAdditionalGuests(prev => prev.filter((_, i) => i !== idx))}
                    style={{ width:26, height:26, borderRadius:6, border:'1.5px solid rgba(255,120,120,0.85)',
                      background:'rgba(220,40,40,0.30)', color:'#FFCCCC', fontSize:14, fontWeight: 700,
                      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight: 1 }}>
                    ×
                  </button>
                </div>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <input data-testid={`ag-first-name-${idx}`}
                    value={ag.first_name} placeholder="First name *"
                    onChange={e => setAdditionalGuests(prev => prev.map((g, i) => i === idx ? { ...g, first_name: e.target.value } : g))}
                    style={{ flex:1, minWidth:0, height:42, padding:'0 10px', borderRadius:8, boxSizing:'border-box',
                      border: showValidation && !ag.first_name.trim()
                        ? '1.5px solid rgba(255,68,68,0.85)'
                        : '1.5px solid rgba(212,168,83,0.55)',
                      background:'rgba(255,255,255,0.95)',
                      fontSize:15, color:'#1A1A1A', fontFamily:"'Cormorant Garamond',serif", outline:'none', fontWeight: 500 }} />
                  <input data-testid={`ag-last-name-${idx}`}
                    value={ag.last_name} placeholder="Last name *"
                    onChange={e => setAdditionalGuests(prev => prev.map((g, i) => i === idx ? { ...g, last_name: e.target.value } : g))}
                    style={{ flex:1, minWidth:0, height:42, padding:'0 10px', borderRadius:8, boxSizing:'border-box',
                      border: showValidation && !ag.last_name.trim()
                        ? '1.5px solid rgba(255,68,68,0.85)'
                        : '1.5px solid rgba(212,168,83,0.55)',
                      background:'rgba(255,255,255,0.95)',
                      fontSize:15, color:'#1A1A1A', fontFamily:"'Cormorant Garamond',serif", outline:'none', fontWeight: 500 }} />
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {['adult', 'child'].map(type => {
                    const isSelected = (type === 'adult' && ag.is_adult === true) || (type === 'child' && ag.is_adult === false);
                    const needsChoice = showValidation && ag.is_adult === null;
                    return (
                      <button key={type} data-testid={`ag-type-${type}-${idx}`}
                        onClick={() => setAdditionalGuests(prev => prev.map((g, i) => i === idx ? { ...g, is_adult: type === 'adult' } : g))}
                        style={{ flex:1, height:38, borderRadius:8, cursor:'pointer',
                          border: isSelected
                            ? '2px solid rgba(212,168,83,0.95)'
                            : (needsChoice ? '1.5px solid rgba(255,68,68,0.85)' : '1.5px solid rgba(255,255,255,0.40)'),
                          background: isSelected ? 'rgba(212,168,83,0.30)' : 'rgba(0,0,0,0.30)',
                          color: isSelected ? '#FFE9A8' : '#FFFFFF',
                          fontSize:13, fontWeight: isSelected ? 700 : 600, fontFamily:"'Cinzel',serif",
                          letterSpacing:'0.6px', textTransform:'uppercase' }}>
                        {type}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Menu tiles */}
        {activeGuests.length > 0 && (
          <div style={{ margin:'18px 0' }}>
            <p style={{ fontFamily:"'Cinzel',serif", fontStyle:'normal',
              fontSize:18, fontWeight:700, letterSpacing:'1.4px',
              color:'#1A1A1A', marginBottom:14, textAlign:'center', textTransform:'uppercase' }}>
              What Will Be Served
            </p>
            <MenuTiles activeMenu={activeMenu} setActiveMenu={setActiveMenu} menuData={menuData || MOCK_MENU}/>
          </div>
        )}

        {error && (
          <p style={{ fontSize:12, color:'#FF9090', textAlign:'center',
            margin:'0 0 10px', fontStyle:'italic' }}>{error}</p>
        )}

        {/* Email validation error — shown when guest tries to submit without email */}
        {emailError && (
          <p data-testid="rsvp-email-error" style={{ fontSize:12, color:'#FF4444', textAlign:'center',
            margin:'0 0 10px', fontStyle:'italic' }}>{emailError}</p>
        )}

        {/* REMOVED: Standalone email field. Email requirement is now enforced via the per-guest email display with red asterisk. */}

        {/* ISSUE #2: No valid token → guide the guest to their personalized link (viewing still allowed). */}
        {!token && (
          <p data-testid="rsvp-no-token-notice" style={{ fontSize:12, color:'rgba(212,185,80,0.92)', textAlign:'center',
            margin:'0 0 10px', fontStyle:'italic', border:'1px solid rgba(212,185,80,0.40)',
            borderRadius:8, padding:'8px 12px', background:'rgba(212,185,80,0.10)' }}>
            Please use your personalized invitation link to RSVP — check your invitation email or text for your link.
          </p>
        )}

        {/* Requirement 7: Button label changes if there are edits */}
        <button onClick={handleSubmit} disabled={submitting || !token} style={{
          width:'100%', height:72, borderRadius:14, marginTop:8, cursor: token ? 'pointer' : 'not-allowed',
          background:'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)',
          border:'1px solid rgba(200,170,60,0.65)',
          boxShadow:'0 4px 16px rgba(0,0,0,0.18), inset 0 1px 0 rgba(212,168,83,0.15)',
          fontFamily:"'Cinzel',serif", fontSize: hasAnyEdits ? 14 : 16,
          fontWeight:700, color:'#D4A853', letterSpacing:'2px', textTransform:'uppercase', outline:'none',
          opacity: (submitting || !token) ? 0.7 : 1,
        }}>
          {submitting ? 'Submitting…' : editMode ? (hasAnyEdits ? 'UPDATE RSVP' : 'UPDATE RSVP') : (hasAnyEdits ? 'SAVE CHANGES & SUBMIT RSVP' : 'SUBMIT RSVP')}
        </button>
      </div>

      {/* Decline flow */}
      <div style={{
        overflow: 'hidden',
        maxHeight: rsvpDecision === 'decline' ? '400px' : '0px',
        opacity:   rsvpDecision === 'decline' ? 1 : 0,
        transition:'max-height 1.0s ease-in-out, opacity 0.6s ease',
      }}>
        <div style={{ display:'flex', flexDirection:'column', gap:12, paddingTop:8 }}>
          <p style={{ fontSize:14, color:WHITE97, lineHeight:1.80, textAlign:'center', margin:0,
            fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontWeight:400,
            textShadow:'0 1px 4px rgba(0,0,0,0.20)' }}>
            We're sorry you're unable to join us.<br/>
            If your plans change, please don't hesitate to reach out.
          </p>
          <textarea placeholder="Leave a message (optional)" rows={2}
            className="placeholder-white/70 rsvp-spec-req"
            style={{ padding:'10px 12px', borderRadius:10, resize:'none',
              border:'1px solid rgba(255,255,255,0.25)', background:'rgba(255,255,255,0.08)',
              boxShadow:'inset 0 1px 0 rgba(255,255,255,0.20)',
              fontSize:12, color:WHITE97, fontFamily:'inherit', outline:'none' }}/>
          {/* REMOVED: Standalone decline email field. Email requirement enforced via per-guest email with red asterisk. */}
          {/* ISSUE #2: No valid token → guide the guest to their personalized link (viewing still allowed). */}
          {!token && (
            <p data-testid="rsvp-no-token-notice-decline" style={{ fontSize:12, color:'rgba(212,185,80,0.92)', textAlign:'center',
              margin:'0 0 2px', fontStyle:'italic', border:'1px solid rgba(212,185,80,0.40)',
              borderRadius:8, padding:'8px 12px', background:'rgba(212,185,80,0.10)' }}>
              Please use your personalized invitation link to RSVP — check your invitation email or text for your link.
            </p>
          )}
          <button onClick={handleSubmit} disabled={submitting || !token} style={{
            width:'100%', height:72, borderRadius:14, border:'none', cursor: token ? 'pointer' : 'not-allowed',
            background:'linear-gradient(145deg,#D4AA50 0%,#C49830 35%,#B48820 70%,#C49830 100%)',
            boxShadow:'0 4px 14px rgba(180,130,20,0.34),inset 0 1.5px 0 rgba(255,235,130,0.40)',
            fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:700,
            color:'#FFF5D0', letterSpacing:'2px', textTransform:'uppercase', outline:'none',
            textShadow:'0 1px 3px rgba(100,60,0,0.25)', opacity: (submitting || !token) ? 0.7 : 1,
          }}>
            {submitting ? 'Submitting…' : editMode ? 'UPDATE RSVP' : 'SUBMIT RSVP'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Resolve display name from guest array + party_name (FIX-01 Feb 2026) ───
// Mirrors lib/resolveGuestGreeting.js priority chain so legacy /rsvp/<token>
// surface produces the SAME string as Compose Invite + InviteLandingPage +
// ExperiencePage for the SAME target. (Feb 2026 multi-surface alignment.)
function resolveDisplayName(guests, partyName) {
  // 1. Admin-typed group_name on any member → verbatim (admin Profile editor
  //    sets this; presence proves admin touched it).
  const adminGroupName = (guests || [])
    .map(g => (g?.group_name || '').trim())
    .find(n => n);
  if (adminGroupName) return adminGroupName;

  // 2. Admin-set party_name wins verbatim — skip CSV-import auto-stamps.
  const pn = (partyName || '').trim();
  if (pn && !/\bParty\s*$/i.test(pn)) return pn;

  // 3-5. Fallbacks when admin didn't type a custom name (or it was auto-stamped).
  if (!guests || guests.length === 0) return '';
  if (guests.length === 1) return guests[0].first_name || '';
  const primary = guests.find(g => g.is_primary === true) ?? guests[0];
  // Match resolveGuestGreeting's logic: only "<lastname> Family" when ALL
  // members share the same last name; otherwise "<primary first> and Guests".
  const lastNames = guests.map(g => (g.last_name || '').trim()).filter(Boolean);
  const uniqueLower = [...new Set(lastNames.map(n => n.toLowerCase()))];
  if (uniqueLower.length === 1 && lastNames.length === guests.length) {
    return `${lastNames[0]} Family`;
  }
  return `${primary.first_name || 'Friend'} and Guests`;
}

// ─── CSS-only balloon burst — large, slow-floating, celebratory ───────────────
const BALLOON_STYLES = `
@keyframes balloonRise {
  0%   { opacity: 0; transform: translateY(0) translateX(0) rotate(0deg); }
  6%   { opacity: 1; }
  88%  { opacity: 0.85; }
  100% { opacity: 0; transform: translateY(-118vh) translateX(var(--drift)) rotate(var(--spin)); }
}
.rsvp-balloon {
  position: fixed;
  bottom: -110px;
  border-radius: 50% 50% 45% 45%;
  animation: balloonRise var(--dur) ease-in-out forwards;
  pointer-events: none;
  z-index: 9999;
}
.rsvp-balloon::after {
  content: '';
  position: absolute;
  bottom: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 1.5px;
  height: 18px;
  background: rgba(120,85,0,0.40);
}`;

const BALLOON_COLORS = [
  '#D4AF37','#E8C84A','rgba(255,235,150,0.93)','rgba(200,168,60,0.90)',
  'rgba(255,255,255,0.78)','#C8963E','#F5D060','rgba(255,220,80,0.88)',
  '#BFA030','rgba(240,200,60,0.92)','#E0B040','rgba(255,245,170,0.85)',
];

// 30 balloons — varied sizes (40–92px), sporadic delays, slow floats
const BALLOON_DATA = [
  { s:58, l:7,  d:0.0, dur:6.5, drift: 38, spin: 12 },
  { s:82, l:18, d:0.6, dur:7.8, drift:-55, spin:-18 },
  { s:44, l:29, d:1.4, dur:5.8, drift: 22, spin:  8 },
  { s:70, l:42, d:0.2, dur:8.2, drift:-42, spin: 22 },
  { s:62, l:54, d:2.0, dur:6.9, drift: 62, spin:-10 },
  { s:88, l:66, d:0.9, dur:7.5, drift:-28, spin: 16 },
  { s:48, l:78, d:2.7, dur:6.1, drift: 48, spin:-20 },
  { s:92, l:90, d:0.4, dur:8.6, drift:-72, spin: 10 },
  { s:52, l:11, d:3.2, dur:6.3, drift: 30, spin:-14 },
  { s:76, l:24, d:1.6, dur:7.7, drift:-52, spin: 25 },
  { s:40, l:36, d:2.4, dur:5.4, drift: 16, spin: -6 },
  { s:66, l:50, d:0.8, dur:8.1, drift:-36, spin: 18 },
  { s:86, l:62, d:3.6, dur:7.3, drift: 56, spin:-22 },
  { s:44, l:74, d:1.1, dur:6.6, drift:-22, spin: 12 },
  { s:74, l:86, d:2.9, dur:8.0, drift: 42, spin:-16 },
  { s:60, l:5,  d:1.8, dur:7.0, drift:-46, spin: 20 },
  { s:84, l:20, d:0.3, dur:8.4, drift: 66, spin: -8 },
  { s:46, l:33, d:2.2, dur:5.9, drift:-32, spin: 14 },
  { s:68, l:47, d:3.4, dur:7.6, drift: 26, spin:-18 },
  { s:56, l:58, d:1.0, dur:6.4, drift:-62, spin: 22 },
  { s:80, l:70, d:1.5, dur:7.2, drift: 46, spin:-12 },
  { s:42, l:82, d:2.8, dur:5.7, drift:-16, spin:  8 },
  { s:90, l:93, d:0.7, dur:8.7, drift: 52, spin: 16 },
  { s:54, l:14, d:3.9, dur:6.7, drift:-42, spin:-24 },
  { s:67, l:27, d:1.3, dur:7.4, drift: 32, spin: 10 },
  { s:85, l:40, d:2.5, dur:8.1, drift:-56, spin:-20 },
  { s:45, l:56, d:0.1, dur:5.6, drift: 72, spin: 18 },
  { s:77, l:68, d:3.1, dur:7.8, drift:-36, spin:-15 },
  { s:50, l:81, d:1.7, dur:6.2, drift: 28, spin: 20 },
  { s:63, l:95, d:2.3, dur:7.1, drift:-48, spin:-10 },
];

function BalloonBurst({ active }) {
  // Inject balloon CSS into document.head so the browser parses it into the CSSOM.
  // The <style> tag inside a React portal fragment is not reliably parsed by browsers.
  useEffect(() => {
    if (!active) return;
    const id = 'rsvp-balloon-styles';
    if (document.getElementById(id)) return; // already injected
    const style = document.createElement('style');
    style.id = id;
    style.textContent = BALLOON_STYLES;
    document.head.appendChild(style);
    return () => { document.getElementById(id)?.remove(); };
  }, [active]);

  if (!active) return null;
  return ReactDOM.createPortal(
    <>
      {BALLOON_DATA.map((b, i) => (
        <div key={i} className="rsvp-balloon" style={{
          left: `${b.l}%`,
          width: b.s,
          height: Math.round(b.s * 1.22),
          background: BALLOON_COLORS[i % BALLOON_COLORS.length],
          animationDelay: `${b.d}s`,
          '--dur': `${b.dur}s`,
          '--drift': `${b.drift}px`,
          '--spin': `${b.spin}deg`,
          boxShadow: `inset -4px -4px 10px rgba(0,0,0,0.18), inset 2px 2px 6px rgba(255,255,255,0.35)`,
        }} />
      ))}
    </>,
    document.body
  );
}
const Botanical = () => (
  <svg width="56" height="200" viewBox="0 0 56 200" fill="none"
    style={{ position:'absolute', right:0, top:'6%', opacity:0.17, pointerEvents:'none' }}>
    <path d="M44 200 C43 152 41 108 39 70 C37 44 34 20 30 5" stroke="#C6A75A" strokeWidth="0.9" fill="none"/>
    <path d="M40 175 C29 162 17 155 4 159" stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M39 142 C27 128 15 122 3 125" stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M38 108 C27 94 16 88 6 91" stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M37 74 C27 60 18 54 10 57" stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M41 155 C49 142 53 129 54 117" stroke="#C6A75A" strokeWidth="0.6" fill="none"/>
    <path d="M40 118 C48 105 52 92 53 80" stroke="#C6A75A" strokeWidth="0.6" fill="none"/>
    <ellipse cx="30" cy="5" rx="3" ry="5.5" stroke="#C6A75A" strokeWidth="0.7" transform="rotate(-12 30 5)" fill="none"/>
    <ellipse cx="30" cy="5" rx="3" ry="5.5" stroke="#C6A75A" strokeWidth="0.7" transform="rotate(12 30 5)" fill="none"/>
    <circle cx="30" cy="3" r="1.8" stroke="#C6A75A" strokeWidth="0.6" fill="none"/>
  </svg>
);

// ─── Main export ───────────────────────────────────────────────────────────────
const RSVPPublicModule = () => {
  const [party,  setParty]  = useState(null);
  const [guests, setGuests] = useState([]);
  const [token,  setToken]  = useState('');
  const [menuData, setMenuData] = useState(MOCK_MENU);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [sharedEditable, setSharedEditable] = useState(false);
  const [rsvpAttribution, setRsvpAttribution] = useState(null);
  const [guestMode, setGuestMode] = useState('unrestricted');

  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/rsvp\/([^/]+)/);
    const params = new URLSearchParams(window.location.search);
    const tok = match ? match[1] : (params.get('token') || '');
    setToken(tok);
    if (tok) {
      fetch(`${API}/api/parties/by-token/${tok}`)
        .then(r => r.json())
        .then(d => {
          if (d.found && d.party) {
            setParty(d.party);
            setGuests(d.guests || []);
            if (d.already_submitted) setAlreadySubmitted(true);
            if (d.shared_editable_rsvp) setSharedEditable(true);
            if (d.rsvp_attribution) setRsvpAttribution(d.rsvp_attribution);
            if (d.additional_guest_mode) setGuestMode(d.additional_guest_mode);
          }
        })
        .catch(() => {});
      // Fetch template-driven menu tiles
      fetch(`${API}/api/public/guest-experience?token=${tok}`)
        .then(r => r.json())
        .then(d => {
          if (d.menu_tiles) setMenuData(templateTilesToMenu(d.menu_tiles));
        })
        .catch(() => {});
    }
  }, []);

  return (
    <div id="rsvp-module"
      style={{ width:'100%', maxWidth:600, margin:'0 auto', padding:'16px 16px 0',
        fontFamily:"'Inter',-apple-system,sans-serif" }}>
      <style>{`
        @keyframes rsvpCardIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {/* Dark glass card */}
      <div style={{
        borderRadius:20, position:'relative',
        background:'rgba(20,15,8,0.58)',
        backdropFilter:'blur(18px) saturate(1.20)',
        WebkitBackdropFilter:'blur(18px) saturate(1.20)',
        border:'1.5px solid rgba(255,255,255,0.18)',
        animation: 'rsvpCardIn 0.8s cubic-bezier(0.22,1,0.36,1) forwards',
        boxShadow:[
          'inset 0 2px 0 rgba(255,255,255,0.14)',
          'inset -1px 0 0 rgba(255,255,255,0.08)',
          'inset 1px 0 0 rgba(255,255,255,0.08)',
          'inset 0 -1px 0 rgba(212,168,83,0.30)',
          '0 32px 64px rgba(0,0,0,0.30)',
          '0 6px 18px rgba(0,0,0,0.18)',
        ].join(', '),
      }}>
        {/* Decorative layer */}
        <div style={{ position:'absolute', inset:0, borderRadius:20, overflow:'hidden',
          pointerEvents:'none', zIndex:1 }}>
          <Botanical/>
          <div style={{ position:'absolute', top:0, left:0, right:0, height:1.5,
            background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.92) 25%,white 50%,rgba(255,255,255,0.92) 75%,transparent)' }}/>
          <div style={{ position:'absolute', top:'-15%', right:'-5%', width:'55%', height:'55%', borderRadius:'50%',
            background:'radial-gradient(circle,rgba(255,255,255,0.28) 0%,rgba(255,255,255,0.08) 40%,transparent 70%)',
            filter:'blur(18px)' }}/>
          <div style={{ position:'absolute', bottom:0, left:0, right:0, height:1,
            background:'linear-gradient(90deg,transparent 5%,rgba(212,168,83,0.50) 30%,rgba(212,168,83,0.50) 70%,transparent 95%)' }}/>
        </div>

        {/* Content */}
        <div style={{ position:'relative', zIndex:2, padding:'32px 24px 28px' }}>
          {(() => {
            // Group-shared editable eligibility check — narrow scope:
            //   shared_editable_rsvp=true AND ≥2 guests AND already submitted.
            const groupEditable = alreadySubmitted && sharedEditable && (guests?.length || 0) >= 2;
            if (alreadySubmitted && !groupEditable) {
              return (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px 0', minHeight:'40vh' }}>
                  <div style={{ width:50, height:50, borderRadius:'50%',
                    background:'linear-gradient(145deg,rgba(235,255,242,0.96),rgba(200,240,218,0.86))',
                    border:'1.5px solid rgba(34,160,90,0.28)',
                    boxShadow:'0 2px 14px rgba(34,160,90,0.16),inset 0 1px 0 rgba(255,255,255,0.80)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:22, color:'#2D7A4F', marginBottom:16 }}>✓</div>
                  <p style={{ fontFamily:"'Cinzel',serif", fontSize:20, fontWeight:700,
                    color:'rgba(255,255,255,0.97)', margin:'0 0 8px', textAlign:'center', letterSpacing:'1.5px' }}>
                    RSVP Already Submitted
                  </p>
                  <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontStyle:'italic',
                    color:'rgba(255,255,255,0.70)', textAlign:'center', lineHeight:1.5, maxWidth:320, margin:0 }}>
                    Your response has been received. If you need to make changes, please contact the host directly.
                  </p>
                </div>
              );
            }
            return (
              <RSVPCard
                party={party}
                guests={guests}
                token={token}
                menuData={menuData}
                guestMode={guestMode}
                editMode={groupEditable}
                attribution={rsvpAttribution}
              />
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export { RSVPCard };
export default RSVPPublicModule;
